import Vue from "vue";
import Vuex from "vuex";

import { cloneDeep, remove } from "lodash-es";

Vue.use(Vuex);

const state = {
  loading: true,
  error: false,
  selections: [],
  max: 5,
  grid: null,
  autoAdjusting: false,
  data: null,
};

const getters = {
  status: ({ loading, error }) => ({ loading, error }),
  autoAdjusting: ({ autoAdjusting }) => autoAdjusting,
  selections: ({ selections }) => selections,
  specs: ({ data }) => (data ? data.specs : null),
  specLabels: ({ data }) => (data ? data.specLabels : null),
  max: ({ max }) => max,
  size: ({ selections }) => selections.length,
  width: ({ selections }) => ({
    flexBasis: `${100 / selections.length}%`,
    maxWidth: `${100 / selections.length}%`,
  }),
  allProducts: ({ data }) => (data ? data.products : null),
  products: (state) =>
    state.selections.map((id) => state.data.products.find((p) => p.id === id)),
};

const mutations = {
  setLoading(state, payload) {
    state.loading = payload;
  },
  setError(state, payload) {
    state.error = payload;
  },
  loadData(state, payload) {
    state.data = payload;
  },
  updateSelection(state, { index, id }) {
    let selections = [...state.selections];
    selections[index] = id;
    state.selections = selections;
  },
  addComparison(state, addition) {
    let next = state.data.products.find(
      (p) => !state.selections.includes(p.id)
    ).id;
    if (addition) return state.selections.push(addition);
    state.selections.push(next);
  },
  removeComparison(state, removal) {
    if (removal) return remove(state.selections, (r) => r === removal);
    if (state.selections.length <= 2) return;
    state.selections.pop();
  },
  setComparison(state, comparison) {
    state.selections = comparison;
  },
  clearComparison(state) {
    state.selections = [];
  },
  setAutoAdjusting(state, payload) {
    state.autoAdjusting = payload;
  },
  setGridElement(state, grid) {
    state.grid = grid;
  },
  setMax(state, max) {
    state.max = max;
  },
  initialize(state, { size, grid }) {
    state.grid = grid;
    state.selections = state.data.products.map((p) => p.id).slice(0, size);
    state.max =
      state.max > state.data.products.length
        ? state.data.products.length
        : state.max;
  },
};

const actions = {
  initialize({ state, commit }, { initial, grid }) {
    commit("setGridElement", grid);
    let debouncer;

    const adjustMax = async () => {
      let selections = cloneDeep(state.selections);
      let products = cloneDeep(state.data.products);
      let max = 0;
      let limit = 5;

      commit("setAutoAdjusting", true);
      commit("clearComparison");

      while (state.selections.length < state.data.products.length && !max) {
        commit("addComparison");
        await Vue.nextTick();
        if (state.selections >= limit) {
          max = limit;
        }
        if (state.grid.scrollWidth > state.grid.offsetWidth) {
          commit("removeComparison");
          max = state.selections.length;
        }
      }

      if (!max) max = state.data.products.length;
      max = Math.min(max, limit);
      commit("setMax", max);

      if (selections.length) commit("setComparison", selections.slice(0, max));
      else commit("setComparison", products.map((p) => p.id).slice(0, initial));
      await Vue.nextTick();

      commit("setAutoAdjusting", false);
    };

    adjustMax();
    window.addEventListener("resize", () => {
      clearTimeout(debouncer);
      debouncer = setTimeout(adjustMax, 100);
    });
  },

  fetchData({ commit }, source) {
    let timeout = 1000;
    let start = Date.now();
    let end, elapsed;
    return new Promise((resolve) =>
      fetch(source)
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            return false;
          }
        })
        .then((data) => {
          if (data) {
            commit("loadData", data);
          } else {
            end = Date.now();
            elapsed = end - start;
            setTimeout(
              () => {
                commit("setError", true);
                resolve();
              },
              elapsed < timeout ? timeout - elapsed : 0
            );
          }
        })
        .catch(() => {
          end = Date.now();
          elapsed = end - start;
          setTimeout(
            () => {
              commit("setError", true);
              resolve();
            },
            elapsed < timeout ? timeout - elapsed : 0
          );
        })
        .finally(() => {
          end = Date.now();
          elapsed = end - start;

          setTimeout(
            () => {
              commit("setLoading", false);
              resolve();
            },
            elapsed < timeout ? timeout - elapsed : 0
          );
        })
    );
  },
};

export default new Vuex.Store({
  state,
  getters,
  mutations,
  actions,
});
